import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [discountCode, setDiscountCode] = useState('');
  const [discountValid, setDiscountValid] = useState(null);
  const [loading, setLoading] = useState(false);

  const plans = {
    monthly: { name: 'Monthly', price: 97, period: '/month' },
    annual: { name: 'Annual', price: 970, period: '/year', badge: 'Best Value' },
    premium: { name: 'Premium', price: 297, period: '/month' }
  };

  const validateDiscount = async () => {
    if (!discountCode) return;
    
    try {
      const response = await axios.post(`${API}/discounts/validate`, { code: discountCode });
      setDiscountValid(response.data);
      toast.success(`${response.data.discount_percent}% discount applied!`);
    } catch (error) {
      setDiscountValid(null);
      toast.error('Invalid discount code');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const signupResponse = await axios.post(`${API}/auth/signup`, formData);
      localStorage.setItem('pivot_token', signupResponse.data.token);
      localStorage.setItem('pivot_user', JSON.stringify(signupResponse.data));
      
      toast.success('Account created!');
      handleCheckout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        plan_type: selectedPlan,
        discount_code: discountCode || null
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    const basePrice = plans[selectedPlan].price;
    if (discountValid) {
      return basePrice - (basePrice * discountValid.discount_percent / 100);
    }
    return basePrice;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black py-12">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png" 
              alt="Pivot To Power" 
              className="w-12 h-12 rounded-full"
            />
            <span className="text-3xl font-cormorant font-bold text-white">Pivot To Power</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-cormorant font-bold bg-gradient-to-r from-amber-300 to-pink-300 bg-clip-text text-transparent mb-4">Join the Network</h1>
          <p className="text-gray-400 font-inter">Select your plan and create your account</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-cormorant font-bold text-white mb-6">Select Your Plan</h2>
            <div className="space-y-4">
              {Object.entries(plans).map(([key, plan]) => (
                <div
                  key={key}
                  onClick={() => setSelectedPlan(key)}
                  className={`bg-white/10 backdrop-blur-sm p-6 cursor-pointer transition-all rounded-xl ${
                    selectedPlan === key ? 'border-2 border-pink-500 shadow-lg shadow-pink-500/30' : 'border border-white/20 hover:border-amber-400/50'
                  }`}
                  data-testid={`plan-${key}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-cormorant font-bold text-white">{plan.name}</h3>
                      {plan.badge && (
                        <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold mt-2 inline-block font-inter">
                          {plan.badge}
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-cormorant font-bold text-amber-400">${plan.price}</span>
                      <span className="text-gray-400 text-sm block font-inter">{plan.period}</span>
                    </div>
                  </div>
                  {selectedPlan === key && <Check className="w-6 h-6 text-pink-400" />}
                </div>
              ))}
            </div>

            <div className="mt-6 bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <h3 className="text-lg font-cormorant font-semibold text-white mb-4 flex items-center space-x-2">
                <Tag className="w-5 h-5 text-amber-400" />
                <span>Have a discount code?</span>
              </h3>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder="FOUNDING50"
                  className="flex-1 bg-black/30 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 outline-none transition-all placeholder:text-gray-500 font-inter"
                  data-testid="discount-code-input"
                />
                <button
                  onClick={validateDiscount}
                  className="bg-gradient-to-r from-amber-400 to-pink-500 hover:from-amber-500 hover:to-pink-600 text-black font-inter font-bold px-6 py-2 rounded-lg transition-all"
                  data-testid="apply-discount-button"
                >
                  Apply
                </button>
              </div>
              {discountValid && (
                <div className="mt-4 p-4 bg-pink-500/20 border border-pink-500/30 rounded-lg">
                  <p className="text-pink-300 font-semibold font-inter">Discount Applied: {discountValid.discount_percent}% off</p>
                  <p className="text-gray-300 text-sm mt-1 font-inter">{discountValid.description}</p>
                </div>
              )}
            </div>

            <div className="mt-6 bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="space-y-2">
                <div className="flex justify-between text-gray-300 font-inter">
                  <span>Subtotal:</span>
                  <span>${plans[selectedPlan].price}</span>
                </div>
                {discountValid && (
                  <div className="flex justify-between text-pink-300 font-inter">
                    <span>Discount ({discountValid.discount_percent}%):</span>
                    <span>-${(plans[selectedPlan].price * discountValid.discount_percent / 100).toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-white/20 pt-2 mt-2" />
                <div className="flex justify-between text-white text-xl font-cormorant font-bold">
                  <span>Total:</span>
                  <span className="text-amber-400">${calculatePrice().toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-cormorant font-bold text-white mb-6">Create Your Account</h2>
            <form onSubmit={handleSignup} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2 font-inter font-medium">Full Name</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 outline-none transition-all font-inter"
                    data-testid="signup-fullname-input"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2 font-inter font-medium">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 outline-none transition-all font-inter"
                    data-testid="signup-email-input"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2 font-inter font-medium">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 outline-none transition-all font-inter"
                    data-testid="signup-password-input"
                  />
                </div>
              </div>

              <div className="mt-6 p-4 bg-purple-900/30 rounded-lg border border-purple-500/20">
                <h4 className="font-cormorant font-semibold text-white mb-2">What You'll Get:</h4>
                <ul className="space-y-2">
                  {[
                    'Full Network Access',
                    'Power Guide Strategic Advisor',
                    'All Courses & Resources',
                    'Power Circles & Community',
                    'Personalized Dashboard',
                    'Lifestyle Design Tools'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center space-x-2 text-gray-300 font-inter">
                      <Check className="w-4 h-4 text-pink-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-inter font-bold py-4 rounded-lg transition-all mt-6 shadow-lg shadow-pink-500/30"
                data-testid="signup-submit-button"
              >
                {loading ? 'Processing...' : 'Continue to Checkout'}
              </button>

              <p className="text-center text-gray-400 text-sm mt-4 font-inter">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-amber-400 hover:text-amber-300 font-semibold"
                  data-testid="go-to-login-link"
                >
                  Login here
                </button>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}